/**
 * `Scene`: a figura como DADO, nao como DOM.
 *
 * Tres retornos concretos dessa escolha:
 *  1. `expect(scene).toMatchSnapshot()` transforma "meu refactor mudou 4000 figuras?"
 *     num diff legivel;
 *  2. a MESMA Scene vira `SVGElement` ao vivo ou string para impressao/PDF -- escolas
 *     vao querer versao em papel, e ela sai de graca;
 *  3. animacao vira patch entre duas Scenes, em vez de reconstrucao de DOM.
 *
 * REGRA CENTRAL: as formas carregam PAPEIS SEMANTICOS, nunca cores. O renderizador
 * emite `class="s-given"`, e o CSS decide a cor. Consequencia: tema claro/escuro custa
 * zero JavaScript, e o snapshot e independente de tema.
 */

import type { Vec2 } from '../geom/vec.ts';
import type { RichText } from '../schema/richtext.ts';

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * O EIXO Y DA CENA CRESCE PARA CIMA; o do SVG, para baixo. Passar de um para o outro é
 * refletir em torno do meio do viewBox, e esta é a constante da reflexão.
 *
 * Existe num lugar só porque são dois os que precisam dela e eles têm de concordar ao
 * pixel: o renderizador (`svg.ts`), que inverte o grupo das formas inteiro, e as
 * camadas interativas (`ui/coords.ts`), que convertem um ponto de cada vez. Com uma
 * cópia em cada lado, mudar uma deixaria o clique caindo espelhado — sem erro nenhum.
 */
export const eixoDoEspelho = (vb: Rect): number => 2 * vb.y + vb.h;

/** Y do modelo → y do SVG, e vice-versa: a reflexão é a sua própria inversa. */
export const refletirY = (vb: Rect, y: number): number => eixoDoEspelho(vb) - y;

/** Papel do traco. Cor e espessura sao decididas pelo CSS. */
export type StrokeRole =
  | 'given' // os dados do problema: solido, espesso
  | 'construction' // trabalho auxiliar de compasso: fino, tracejado, apagado
  | 'drawn' // reta, semirreta ou segmento traçado pelo aluno: contínuo, bem visível
  | 'result' // o objeto-resposta: cor de destaque
  | 'auxiliary' // malha, eixos, cotas
  | 'highlight'
  | 'error'
  | 'muted'
  | 'guide'; // linha de leitura: do eixo ate um valor que nao cai na escala

/**
 * Preenchimentos. Os terminados em `-fill` sao veus de baixa opacidade sobre o papel;
 * `ink` e o unico solido, para pecas discretas que precisam ler como CHEIAS ao lado de
 * pecas vazias -- uma bola escura ao lado de uma clara, uma casa ocupada ao lado de uma
 * livre. Nao e cor: e tinta, e some no tema escuro junto com o resto.
 */
export type FillRole =
  'none' | 'surface' | 'ink' | 'given-fill' | 'result-fill' | 'error-fill' | 'muted-fill';

export type PointRole = 'given' | 'derived' | 'free' | 'result' | 'ghost';

export type Shape =
  | {
      readonly t: 'seg';
      readonly a: Vec2;
      readonly b: Vec2;
      readonly role: StrokeRole;
      readonly arrow?: 'none' | 'end' | 'both';
    }
  | { readonly t: 'ray'; readonly from: Vec2; readonly through: Vec2; readonly role: StrokeRole }
  | { readonly t: 'line'; readonly p: Vec2; readonly dir: Vec2; readonly role: StrokeRole }
  | {
      readonly t: 'circle';
      readonly c: Vec2;
      readonly r: number;
      readonly role: StrokeRole;
      readonly fill?: FillRole;
    }
  | {
      readonly t: 'arc';
      readonly c: Vec2;
      readonly r: number;
      readonly a0: number;
      readonly a1: number;
      readonly role: StrokeRole;
    }
  | {
      readonly t: 'poly';
      readonly pts: readonly Vec2[];
      readonly closed: boolean;
      readonly role: StrokeRole;
      readonly fill?: FillRole;
    }
  | { readonly t: 'rect'; readonly at: Rect; readonly role: StrokeRole; readonly fill?: FillRole }
  | { readonly t: 'point'; readonly p: Vec2; readonly role: PointRole; readonly name?: string }
  | {
      readonly t: 'angle';
      readonly vertex: Vec2;
      readonly a: Vec2;
      readonly b: Vec2;
      readonly ticks: 1 | 2 | 3;
      readonly label?: RichText;
      readonly role?: StrokeRole;
    }
  | { readonly t: 'right-angle'; readonly vertex: Vec2; readonly a: Vec2; readonly b: Vec2 }
  | { readonly t: 'ticks'; readonly a: Vec2; readonly b: Vec2; readonly n: 1 | 2 | 3 }
  | {
      readonly t: 'dimension';
      readonly a: Vec2;
      readonly b: Vec2;
      readonly offset: number;
      readonly label: RichText;
    }
  | {
      readonly t: 'label';
      readonly at: Vec2;
      readonly text: RichText;
      readonly anchor: LabelAnchor;
      readonly role?: StrokeRole;
      /** Rotacao em graus, anti-horaria. Usada em titulo de eixo vertical. */
      readonly rotate?: number;
      /** Enfase: titulo de grafico, titulo de eixo, ou numero de marca de eixo. */
      readonly emphasis?: LabelEmphasis;
    }
  | { readonly t: 'grid'; readonly step: number; readonly extent: Rect; readonly dots: boolean }
  | {
      readonly t: 'axes';
      readonly extent: Rect;
      readonly step: number;
      readonly xLabel?: string;
      readonly yLabel?: string;
    }
  | {
      readonly t: 'group';
      readonly children: readonly Shape[];
      readonly opacity?: number;
      readonly key?: string;
    };

export type LabelAnchor = 'auto' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | 'c';

/**
 * `tick` e o numero de uma marca de eixo: reto, na fonte da interface e menor que um
 * rotulo de ponto. Em italico serifado, como os rotulos "A" e "B", um "−3" se lia
 * como nome de objeto, nao como coordenada.
 */
/** `incognita`: o "?" no lugar de um valor desconhecido — grande, para não passar despercebido. */
export type LabelEmphasis = 'title' | 'axis' | 'tick' | 'incognita';

export interface Scene {
  readonly viewBox: Rect;
  /** Unidades de modelo por unidade de viewBox; guia espessuras e tamanhos de fonte. */
  readonly unit: number;
  readonly shapes: readonly Shape[];
  /**
   * OBRIGATORIO, nao opcional. Uma figura puramente visual exclui alunos de baixa
   * visao; o tipo forca o autor a escrever a descricao. Serve tambem ao caminho de
   * impressao e a exportacao em braille.
   */
  readonly a11y: { readonly title: string; readonly desc: string };
}

// ---------------------------------------------------------------------------
// Construcao de cena
// ---------------------------------------------------------------------------

/** Percorre a arvore de formas, inclusive dentro de grupos. */
export function* walk(shapes: readonly Shape[]): Generator<Shape> {
  for (const s of shapes) {
    yield s;
    if (s.t === 'group') yield* walk(s.children);
  }
}

/** Todos os pontos relevantes para calcular limites. */
function* significantPoints(shapes: readonly Shape[]): Generator<Vec2> {
  for (const s of walk(shapes)) {
    switch (s.t) {
      case 'seg':
        yield s.a;
        yield s.b;
        break;
      case 'ray':
        yield s.from;
        yield s.through;
        break;
      case 'circle':
        yield { x: s.c.x - s.r, y: s.c.y - s.r };
        yield { x: s.c.x + s.r, y: s.c.y + s.r };
        break;
      case 'arc':
        yield { x: s.c.x - s.r, y: s.c.y - s.r };
        yield { x: s.c.x + s.r, y: s.c.y + s.r };
        break;
      case 'poly':
        yield* s.pts;
        break;
      case 'rect':
        yield { x: s.at.x, y: s.at.y };
        yield { x: s.at.x + s.at.w, y: s.at.y + s.at.h };
        break;
      case 'point':
        yield s.p;
        break;
      case 'angle':
        yield s.vertex;
        yield s.a;
        yield s.b;
        break;
      case 'right-angle':
        yield s.vertex;
        break;
      case 'ticks':
        yield s.a;
        yield s.b;
        break;
      case 'dimension':
        yield s.a;
        yield s.b;
        break;
      case 'label':
        yield s.at;
        break;
      case 'grid':
      case 'axes':
        yield { x: s.extent.x, y: s.extent.y };
        yield { x: s.extent.x + s.extent.w, y: s.extent.y + s.extent.h };
        break;
      case 'line':
      case 'group':
        break;
    }
  }
}

/**
 * viewBox calculado a partir do conteudo, com folga proporcional.
 * NUNCA use viewBox fixo: e a origem de 90% das figuras cortadas ou perdidas no canto.
 */
export function boundsOf(shapes: readonly Shape[], padRatio = 0.08): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of significantPoints(shapes)) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 100, h: 100 };

  const w = Math.max(maxX - minX, 1e-6);
  const h = Math.max(maxY - minY, 1e-6);
  const pad = Math.max(w, h) * padRatio;

  return { x: minX - pad, y: minY - pad, w: w + 2 * pad, h: h + 2 * pad };
}

export interface SceneOptions {
  readonly a11y: { readonly title: string; readonly desc: string };
  readonly viewBox?: Rect;
  readonly padRatio?: number;
}

export function scene(shapes: readonly Shape[], opts: SceneOptions): Scene {
  const viewBox = opts.viewBox ?? boundsOf(shapes, opts.padRatio ?? 0.08);
  return {
    viewBox,
    unit: Math.max(viewBox.w, viewBox.h) / 100,
    shapes,
    a11y: opts.a11y,
  };
}

// --- atalhos de construcao -------------------------------------------------

export const seg = (a: Vec2, b: Vec2, role: StrokeRole = 'given'): Shape => ({
  t: 'seg',
  a,
  b,
  role,
});
export const ray = (from: Vec2, through: Vec2, role: StrokeRole = 'given'): Shape => ({
  t: 'ray',
  from,
  through,
  role,
});
export const circ = (
  c: Vec2,
  r: number,
  role: StrokeRole = 'construction',
  fill?: FillRole,
): Shape => (fill === undefined ? { t: 'circle', c, r, role } : { t: 'circle', c, r, role, fill });
export const poly = (
  pts: readonly Vec2[],
  closed = true,
  role: StrokeRole = 'given',
  fill?: FillRole,
): Shape =>
  fill === undefined ? { t: 'poly', pts, closed, role } : { t: 'poly', pts, closed, role, fill };
export const pt = (p: Vec2, role: PointRole = 'given', name?: string): Shape =>
  name === undefined ? { t: 'point', p, role } : { t: 'point', p, role, name };
export const label = (at: Vec2, text: RichText, anchor: LabelAnchor = 'auto'): Shape => ({
  t: 'label',
  at,
  text,
  anchor,
});
export const group = (children: readonly Shape[], key?: string): Shape =>
  key === undefined ? { t: 'group', children } : { t: 'group', children, key };
